import { useState, useEffect, useRef } from "react";

const C = {
  bg: "#ffffff", bg2: "#f8f9fa", bg3: "#f1f3f5", bg4: "#e9ecef",
  gold: "#d97706", goldL: "#f59e0b",
  blueL: "#3b82f6", blue2: "#2563eb",
  greenL: "#10b981", greenLL: "#059669",
  redL: "#ef4444", redLL: "#dc2626",
  purpleL: "#8b5cf6", tealL: "#14b8a6",
  orange: "#f97316", orangeL: "#fb923c",
  pink: "#ec4899", pinkL: "#f9a8d4",
  white: "#111827", text: "#374151", muted: "#6b7280",
  border: "rgba(0,0,0,0.1)", borderHi: "rgba(0,0,0,0.15)",
};

function useReveal() {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.06 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, vis];
}
function Reveal({ children, delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "none" : "translateY(20px)", transition: `opacity 0.55s ${delay}s ease, transform 0.55s ${delay}s ease` }}>
      {children}
    </div>
  );
}

function HR() { return <div style={{ height: 1, background: C.border, margin: "60px 0" }} />; }

function SecLabel({ num, title, color = C.blueL }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
      <span style={{ fontFamily: "monospace", fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.18em", color: C.muted }}>SECTION {num}</span>
      <div style={{ flex: 1, height: 1, background: C.border }} />
      <span style={{ fontFamily: "monospace", fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.12em", color, textTransform: "uppercase" }}>{title}</span>
    </div>
  );
}

function GameBadge({ icon, title, color, sub }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14, marginBottom: 18 }}>
      <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}18`, border: `1px solid ${color}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: C.white, marginBottom: 3 }}>{title}</div>
        {sub && <div style={{ fontSize: "0.77rem", color: C.muted, lineHeight: 1.6 }}>{sub}</div>}
      </div>
    </div>
  );
}

// ══════════════════════
// GAME 1 — Regulator Match
// ══════════════════════
const REGS = [
  { id: "csa", name: "CSA", color: C.gold },
  { id: "ciro", name: "CIRO", color: C.blueL },
  { id: "cipf", name: "CIPF", color: C.greenL },
  { id: "fintrac", name: "FINTRAC", color: C.purpleL },
  { id: "obsi", name: "OBSI", color: C.tealL },
  { id: "osfi", name: "OSFI", color: C.redL },
];
const CLUES = [
  { id: "csa", clue: "Umbrella body coordinating all 13 provincial regulators. Issues National Instruments. Runs the Passport System." },
  { id: "ciro", clue: "National SRO formed Jan 1, 2023 (MFDA + IIROC merged). Enforces IDPC Rules & UMIR for all member firms." },
  { id: "cipf", clue: "Industry-funded insurance protecting client assets up to $1M per account category on member firm insolvency." },
  { id: "fintrac", clue: "Canada's AML/ATF intelligence unit. Receives Large Cash Reports (≥$10K) and Suspicious Transaction Reports." },
  { id: "obsi", clue: "Free, independent dispute resolution for clients after the dealer's internal complaint process has failed." },
  { id: "osfi", clue: "Prudential regulator for federally chartered banks and insurers. Enforces the Bank Act. Ensures financial stability." },
];
const shuffle = arr => [...arr].sort(() => Math.random() - 0.5);

function RegulatorMatch() {
  const [clues] = useState(() => shuffle(CLUES));
  const [selected, setSelected] = useState(null);
  const [matches, setMatches] = useState({});
  const [wrong, setWrong] = useState(null);
  const [done, setDone] = useState(false);

  function pickClue(id) { if (matches[id]) return; setSelected(id); setWrong(null); }
  function pickReg(id) {
    if (!selected) return;
    if (selected === id) {
      const m = { ...matches, [selected]: id }; setMatches(m); setSelected(null);
      if (Object.keys(m).length === clues.length) setDone(true);
    } else { setWrong(id); setTimeout(() => setWrong(null), 650); }
  }
  function reset() { setSelected(null); setMatches({}); setWrong(null); setDone(false); }

  return (
    <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, padding: 26 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <p style={{ fontSize: "0.79rem", color: C.muted, lineHeight: 1.55, flex: 1 }}>
          {selected ? `Now click the matching badge for: "${clues.find(c => c.id === selected)?.clue.slice(0, 52)}…"` : "Step 1 — click a description. Step 2 — click the matching regulator badge."}
        </p>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexShrink: 0 }}>
          <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: C.gold }}>{Object.keys(matches).length}/{clues.length}</span>
          <button onClick={reset} style={{ background: "transparent", border: `1px solid ${C.borderHi}`, color: C.muted, borderRadius: 6, padding: "3px 11px", cursor: "pointer", fontSize: "0.68rem", fontFamily: "inherit" }}>↺</button>
        </div>
      </div>

      {done && (
        <div style={{ background: `${C.greenL}10`, border: `1px solid ${C.greenL}40`, borderRadius: 8, padding: "11px 16px", marginBottom: 14, textAlign: "center", fontFamily: "Georgia,serif", fontSize: "0.9rem", color: C.greenLL }}>
          🏆 All 6 matched — perfect!
        </div>
      )}

      {/* Badges */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 16 }}>
        {REGS.map(r => {
          const isMatched = Object.values(matches).includes(r.id);
          return (
            <button key={r.id} onClick={() => pickReg(r.id)} disabled={isMatched}
              style={{ padding: "7px 15px", background: isMatched ? `${r.color}12` : C.bg4, border: `2px solid ${isMatched ? r.color : wrong === r.id ? C.redL : selected ? r.color + "55" : C.border}`, borderRadius: 8, cursor: isMatched ? "default" : "pointer", fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 800, color: isMatched ? r.color : C.white, transition: "all 0.14s", opacity: isMatched ? 0.55 : 1, transform: wrong === r.id ? "scale(0.94)" : "none" }}>
              {isMatched ? "✓ " : ""}{r.name}
            </button>
          );
        })}
      </div>

      {/* Clue cards */}
      <div style={{ display: "grid", gap: 7 }}>
        {clues.map(cl => {
          const isMatched = !!matches[cl.id];
          const isSelected = selected === cl.id;
          const col = isMatched ? REGS.find(r => r.id === cl.id)?.color : null;
          return (
            <div key={cl.id} onClick={() => !isMatched && pickClue(cl.id)}
              style={{ background: isMatched ? `${col}0c` : isSelected ? C.bg4 : C.bg2, border: `1px solid ${isMatched ? col + "44" : isSelected ? C.goldL + "55" : C.border}`, borderRadius: 8, padding: "10px 14px", cursor: isMatched ? "default" : "pointer", display: "flex", alignItems: "center", gap: 10, transition: "all 0.18s" }}>
              <span style={{ fontFamily: "monospace", fontSize: "0.63rem", fontWeight: 800, color: isMatched ? col : isSelected ? C.goldL : C.muted, width: 54, flexShrink: 0 }}>{isMatched ? REGS.find(r => r.id === cl.id)?.name : isSelected ? "ACTIVE" : "???"}</span>
              <span style={{ fontSize: "0.81rem", color: C.text, lineHeight: 1.55 }}>{cl.clue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════
// GAME 2 — ML Stages Sorter
// ══════════════════════
const ML_SCS = [
  { text: "A criminal deposits $9,000 daily at multiple branches to avoid the reporting threshold — called 'structuring.'", stage: "placement", why: "Cash entering the financial system in small repeated deposits to evade detection = Placement." },
  { text: "Funds are wired through 5 shell companies across different countries, each taking a small cut.", stage: "layering", why: "Complex cross-border transactions designed to obscure the money's origin = Layering." },
  { text: "Laundered money is used to buy into a legitimate restaurant chain that now generates taxable revenue.", stage: "integration", why: "Cleaned funds re-entering the economy as a seemingly legitimate business investment = Integration." },
  { text: "A casino patron buys $50,000 in chips with cash, then immediately cashes out via a cheque.", stage: "placement", why: "Converting illegal cash into a financial instrument at the point of entry = Placement." },
  { text: "Securities are traded back and forth between controlled accounts to create a false paper trail of 'gains.'", stage: "layering", why: "Using financial market transactions to generate fictitious legitimate-looking records = Layering." },
  { text: "A lawyer receives unexplained third-party funds to purchase real estate 'on behalf of a client.'", stage: "integration", why: "Using laundered funds to acquire real property that now appears as a legitimate asset = Integration." },
];
const STAGES = [
  { id: "placement", label: "Placement", icon: "💵", color: C.redL, desc: "Illegal cash enters the system" },
  { id: "layering", label: "Layering", icon: "🔀", color: C.gold, desc: "Transactions obscure the trail" },
  { id: "integration", label: "Integration", icon: "🏢", color: C.greenL, desc: "Clean money re-enters economy" },
];

function MLSorter() {
  const [cur, setCur] = useState(0);
  const [score, setScore] = useState(0);
  const [fb, setFb] = useState(null);
  const [done, setDone] = useState(false);

  function guess(id) {
    if (fb) return;
    const ok = id === ML_SCS[cur].stage;
    if (ok) setScore(s => s + 1);
    setFb({ ok, correct: ML_SCS[cur].stage, why: ML_SCS[cur].why });
    setTimeout(() => { setFb(null); if (cur < ML_SCS.length - 1) setCur(c => c + 1); else setDone(true); }, 2000);
  }
  function reset() { setCur(0); setScore(0); setFb(null); setDone(false); }

  return (
    <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, padding: 26 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 7, marginBottom: 18 }}>
        {STAGES.map(s => (
          <div key={s.id} style={{ background: `${s.color}0d`, border: `1px solid ${s.color}28`, borderRadius: 8, padding: "9px 10px", textAlign: "center" }}>
            <div style={{ fontSize: "1.1rem" }}>{s.icon}</div>
            <div style={{ fontFamily: "monospace", fontSize: "0.68rem", fontWeight: 800, color: s.color, marginTop: 3 }}>{s.label}</div>
            <div style={{ fontSize: "0.65rem", color: C.muted, marginTop: 2, lineHeight: 1.4 }}>{s.desc}</div>
          </div>
        ))}
      </div>

      {done ? (
        <div style={{ textAlign: "center", padding: "22px 0" }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", color: C.white, marginBottom: 8 }}>{score}/{ML_SCS.length} correct {score === ML_SCS.length ? "🏆" : "📚"}</div>
          <div style={{ fontSize: "0.82rem", color: C.muted, marginBottom: 18 }}>{score === ML_SCS.length ? "Perfect — all three stages mastered." : "Review Placement → Layering → Integration and try again."}</div>
          <button onClick={reset} style={{ background: C.gold, color: C.bg, border: "none", padding: "9px 22px", fontFamily: "inherit", fontWeight: 800, fontSize: "0.8rem", borderRadius: 100, cursor: "pointer" }}>↺ Play Again</button>
        </div>
      ) : (
        <>
          <div style={{ background: C.bg4, border: `1px solid ${fb ? (fb.ok ? C.greenL : C.redL) + "44" : C.borderHi}`, borderRadius: 10, padding: "15px 18px", marginBottom: 14, minHeight: 70, transition: "border-color 0.2s" }}>
            <div style={{ fontFamily: "monospace", fontSize: "0.6rem", color: C.muted, marginBottom: 7 }}>SCENARIO {cur + 1} OF {ML_SCS.length}</div>
            <div style={{ fontSize: "0.88rem", color: C.text, lineHeight: 1.7 }}>{ML_SCS[cur].text}</div>
            {fb && <div style={{ marginTop: 10, fontSize: "0.79rem", color: fb.ok ? C.greenLL : C.redLL, lineHeight: 1.6 }}>{fb.ok ? "✓ Correct! " : `✗ This is ${STAGES.find(s => s.id === fb.correct)?.label}. `}{fb.why}</div>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
            {STAGES.map(s => (
              <button key={s.id} onClick={() => guess(s.id)} disabled={!!fb}
                style={{ padding: "11px 8px", background: C.bg2, border: `2px solid ${C.border}`, borderRadius: 10, cursor: fb ? "default" : "pointer", fontFamily: "inherit", fontSize: "0.77rem", fontWeight: 700, color: s.color, textAlign: "center" }}>
                <div style={{ fontSize: "1.1rem", marginBottom: 5 }}>{s.icon}</div>{s.label}
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 4, marginTop: 12 }}>
            {ML_SCS.map((_, i) => <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < cur ? C.greenL : i === cur ? C.gold : C.bg4, transition: "background 0.3s" }} />)}
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════
// GAME 3 — FINTRAC Trainer
// ══════════════════════
const FT_SCS = [
  { scenario: "A new client deposits $12,500 in cash to open their account.", answer: "lctr", why: "Single cash deposit ≥$10,000 → Large Cash Transaction Report required." },
  { scenario: "Three cash deposits in one day: $4,200 at 9AM · $3,800 at noon · $2,500 at 4PM.", answer: "lctr", why: "Total = $10,500 in 24 hours. Cash is aggregated — ≥$10,000 → LCTR required." },
  { scenario: "A client's name exactly matches an entry on a government terrorist financing sanctions list.", answer: "tpr", why: "No dollar minimum — any property linked to terrorist financing → Terrorist Property Report immediately." },
  { scenario: "A client makes repeated $9,500 cash deposits over several weeks, always just under $10,000.", answer: "str", why: "'Structuring' — deliberately staying under the threshold — is a classic ML red flag. No dollar minimum for an STR." },
  { scenario: "A regular client deposits a $3,200 payroll cheque. Nothing unusual about the transaction.", answer: "none", why: "Under $10,000 by cheque, no suspicious indicators, no sanctions hit → No FINTRAC report needed." },
  { scenario: "A new client says they sold property abroad for cash and wants to invest $8,500 — no documentation provided.", answer: "str", why: "Unexplained foreign cash proceeds without documentation is suspicious regardless of amount → STR." },
];
const FT_OPTS = [
  { id: "lctr", label: "Large Cash Report", icon: "💵", color: C.gold },
  { id: "str", label: "Suspicious Transaction", icon: "🔍", color: C.redL },
  { id: "tpr", label: "Terrorist Property", icon: "🚨", color: C.purpleL },
  { id: "none", label: "No Report Needed", icon: "✅", color: C.greenL },
];

function FINTRACTrainer() {
  const [cur, setCur] = useState(0);
  const [fb, setFb] = useState(null);
  const [score, setScore] = useState(0);
  const [done, setDone] = useState(false);

  function guess(id) {
    if (fb) return;
    const ok = id === FT_SCS[cur].answer;
    if (ok) setScore(s => s + 1);
    setFb({ ok, id, correct: FT_SCS[cur].answer, why: FT_SCS[cur].why });
  }
  function next() { setFb(null); if (cur < FT_SCS.length - 1) setCur(c => c + 1); else setDone(true); }
  function reset() { setCur(0); setScore(0); setFb(null); setDone(false); }
  const pct = Math.round(score / FT_SCS.length * 100);

  return (
    <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, padding: 26 }}>
      {done ? (
        <div style={{ textAlign: "center", padding: "22px 0" }}>
          <div style={{ width: 80, height: 80, borderRadius: "50%", background: `conic-gradient(${C.gold} 0% ${pct}%, ${C.bg4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", position: "relative" }}>
            <div style={{ position: "absolute", inset: 9, borderRadius: "50%", background: C.bg3 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1rem", fontWeight: 700, color: C.gold, zIndex: 1 }}>{score}/{FT_SCS.length}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: C.white, marginBottom: 16 }}>{score >= 5 ? "Strong compliance instincts! ✦" : "Review FINTRAC thresholds — try again 📚"}</div>
          <button onClick={reset} style={{ background: C.gold, color: C.bg, border: "none", padding: "9px 22px", fontFamily: "inherit", fontWeight: 800, fontSize: "0.8rem", borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      ) : (
        <>
          <div style={{ background: C.bg4, border: `1px solid ${C.borderHi}`, borderRadius: 10, padding: "15px 18px", marginBottom: 14 }}>
            <div style={{ fontFamily: "monospace", fontSize: "0.6rem", color: C.muted, marginBottom: 7 }}>SCENARIO {cur + 1} OF {FT_SCS.length}</div>
            <div style={{ fontSize: "0.88rem", color: C.text, lineHeight: 1.7 }}>{FT_SCS[cur].scenario}</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 8, marginBottom: 12 }}>
            {FT_OPTS.map(opt => {
              let bdr = C.border, bg = C.bg2, col = C.white;
              if (fb) {
                if (opt.id === FT_SCS[cur].answer) { bg = `${opt.color}12`; bdr = opt.color + "66"; col = opt.color; }
                else if (opt.id === fb.id && !fb.ok) { bg = `${C.redL}0d`; bdr = C.redL + "44"; col = C.redLL; }
              }
              return (
                <button key={opt.id} onClick={() => guess(opt.id)} disabled={!!fb}
                  style={{ padding: "12px 10px", background: bg, border: `2px solid ${bdr}`, borderRadius: 10, cursor: fb ? "default" : "pointer", fontFamily: "inherit", fontSize: "0.75rem", fontWeight: 700, color: col, transition: "all 0.18s", textAlign: "center" }}>
                  <div style={{ fontSize: "1.1rem", marginBottom: 4 }}>{opt.icon}</div>{opt.label}
                </button>
              );
            })}
          </div>
          {fb && (
            <>
              <div style={{ background: fb.ok ? `${C.greenL}0d` : `${C.redL}0d`, border: `1px solid ${fb.ok ? C.greenL : C.redL}33`, borderRadius: 8, padding: "11px 14px", fontSize: "0.81rem", color: fb.ok ? C.greenLL : C.redLL, lineHeight: 1.65, marginBottom: 12 }}>
                {fb.ok ? "✓ Correct! " : `✗ Answer: ${FT_OPTS.find(o => o.id === fb.correct)?.label}. `}{fb.why}
              </div>
              <button onClick={next} style={{ background: C.gold, color: C.bg, border: "none", padding: "9px 22px", fontFamily: "inherit", fontWeight: 800, fontSize: "0.8rem", borderRadius: 100, cursor: "pointer" }}>
                {cur < FT_SCS.length - 1 ? "Next Scenario →" : "See Score →"}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════
// GAME 4 — Flashcards
// ══════════════════════
const CARDS = [
  { q: "What does CIPF cover?", a: "Client cash and securities up to $1M per account category (general, retirement, RESP) — ONLY on member firm insolvency. Does not cover market losses." },
  { q: "What is UMIR?", a: "Universal Market Integrity Rules — CIRO's rules governing ALL trading on Canadian marketplaces. Prevents manipulation, wash trading, and unfair practices." },
  { q: "What is an ATS (dark pool)?", a: "Alternative Trading System — trades already-listed securities anonymously. Large institutions use them to execute big orders without revealing intent and moving the market." },
  { q: "What is CDS?", a: "Canadian Depository for Securities — clears and settles all stock and bond trades, T+1 cycle. Acts as central counterparty, eliminating default risk." },
  { q: "What is CDCC?", a: "Canadian Derivatives Clearing Corporation — central counterparty for all exchange-traded options and futures. Guarantees every derivatives contract." },
  { q: "What does CASL require?", a: "Canada's Anti-Spam Law requires every commercial electronic message to include: (1) Consent, (2) Sender identification, (3) A working unsubscribe mechanism." },
  { q: "Regulatory vs. Criminal proof standard?", a: "Regulatory (CIRO/OSC): 'balance of probabilities.' Criminal (RCMP/courts): 'beyond a reasonable doubt.' Only courts can impose imprisonment — not CIRO." },
  { q: "Three stages of money laundering?", a: "1. Placement — illegal cash enters the financial system. 2. Layering — complex transactions obscure the source. 3. Integration — clean funds re-enter the economy." },
  { q: "OSFI's role?", a: "Prudential regulator for federally chartered banks and insurers. Ensures financial stability. Enforces the Bank Act including the in-branch insurance sale ban." },
  { q: "What are IDPC Rules?", a: "CIRO's rules governing business conduct, client relationships, and suitability for investment dealers — the day-to-day rulebook for how advisors must treat clients." },
  { q: "What is counterparty risk?", a: "Risk the other side of a trade defaults before settlement. Eliminated by clearing agencies — CDS (stocks/bonds) and CDCC (derivatives) become buyer to every seller." },
  { q: "What is OBSI?", a: "Ombudsman for Banking Services and Investments — free, independent dispute resolution after the dealer's internal complaint process fails. Non-binding but influential." },
];

function Flashcards() {
  const [idx, setIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [known, setKnown] = useState(new Set());
  const [review, setReview] = useState(new Set());

  function go(dir) { setFlipped(false); setTimeout(() => setIdx(i => (i + dir + CARDS.length) % CARDS.length), 120); }
  function markKnown() { setKnown(s => new Set([...s, idx])); go(1); }
  function markReview() { setReview(s => new Set([...s, idx])); go(1); }

  return (
    <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, padding: 26 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {CARDS.map((_, i) => (
            <div key={i} onClick={() => { setFlipped(false); setIdx(i); }}
              style={{ width: 9, height: 9, borderRadius: "50%", cursor: "pointer", background: known.has(i) ? C.greenL : review.has(i) ? C.redL : i === idx ? C.gold : C.bg4, transition: "background 0.2s" }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <span style={{ fontFamily: "monospace", fontSize: "0.65rem", color: C.greenL }}>✓ {known.size}</span>
          <span style={{ fontFamily: "monospace", fontSize: "0.65rem", color: C.redL }}>↺ {review.size}</span>
          <span style={{ fontFamily: "monospace", fontSize: "0.65rem", color: C.muted }}>{idx + 1}/{CARDS.length}</span>
        </div>
      </div>
      <div onClick={() => setFlipped(f => !f)}
        style={{ background: C.bg4, border: `1px solid ${flipped ? C.gold + "44" : C.borderHi}`, borderRadius: 12, padding: "28px 22px", textAlign: "center", cursor: "pointer", minHeight: 108, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", transition: "border-color 0.2s", marginBottom: 14 }}>
        <div style={{ fontFamily: "monospace", fontSize: "0.58rem", color: flipped ? C.gold : C.muted, letterSpacing: "0.12em", marginBottom: 12 }}>
          {flipped ? "ANSWER — click to flip back" : `CARD ${idx + 1} — click to reveal`}
        </div>
        <div style={{ fontFamily: flipped ? "inherit" : "Georgia,serif", fontSize: flipped ? "0.84rem" : "1rem", color: C.white, lineHeight: 1.72, maxWidth: 500 }}>
          {flipped ? CARDS[idx].a : CARDS[idx].q}
        </div>
      </div>
      <div style={{ display: "flex", gap: 7, justifyContent: "center", flexWrap: "wrap" }}>
        <button onClick={() => go(-1)} style={{ background: C.bg2, color: C.muted, border: `1px solid ${C.border}`, padding: "8px 15px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.74rem" }}>← Prev</button>
        {flipped && <>
          <button onClick={markKnown} style={{ background: `${C.greenL}12`, color: C.greenL, border: `1px solid ${C.greenL}3a`, padding: "8px 15px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.74rem", fontWeight: 700 }}>✓ Got it</button>
          <button onClick={markReview} style={{ background: `${C.redL}0e`, color: C.redL, border: `1px solid ${C.redL}3a`, padding: "8px 15px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.74rem", fontWeight: 700 }}>↺ Review</button>
        </>}
        <button onClick={() => go(1)} style={{ background: C.bg2, color: C.muted, border: `1px solid ${C.border}`, padding: "8px 15px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", fontSize: "0.74rem" }}>Next →</button>
      </div>
    </div>
  );
}

// ══════════════════════
// Trade Lifecycle
// ══════════════════════
function TradeFlow() {
  const [step, setStep] = useState(null);
  const steps = [
    { icon: "👤", label: "Investor", color: C.blueL, desc: "Places a buy or sell order through their registered dealer." },
    { icon: "🏢", label: "Dealer", color: C.gold, desc: "Routes the order to the appropriate marketplace (exchange or ATS)." },
    { icon: "🏛️", label: "Exchange / ATS", color: C.purpleL, desc: "Exchange (TSX): Public venue — price discovery and liquidity.\nATS (Dark Pool): Anonymous large institutional orders to reduce market impact." },
    { icon: "⚙️", label: "Clearing", color: C.tealL, desc: "CDS: Clears stocks and bonds — becomes central counterparty, eliminates default risk.\nCDCC: Clears all derivatives (options, futures), guarantees every contract." },
    { icon: "✅", label: "Settlement T+1", color: C.greenL, desc: "Securities and cash change hands 1 business day after the trade date. Ownership officially transfers." },
    { icon: "🔒", label: "Custody", color: C.muted, desc: "CDS holds all securities electronically in book-entry form — no physical share certificates required." },
  ];
  return (
    <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, padding: 26 }}>
      <p style={{ fontSize: "0.77rem", color: C.muted, marginBottom: 18 }}>Click any step in the trade lifecycle for more detail.</p>
      <div style={{ display: "flex", alignItems: "center", overflowX: "auto", paddingBottom: 4, marginBottom: 16 }}>
        {steps.map((s, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div onClick={() => setStep(step === i ? null : i)} style={{ display: "flex", flexDirection: "column", alignItems: "center", cursor: "pointer", padding: "0 8px" }}>
              <div style={{ width: 42, height: 42, borderRadius: "50%", background: step === i ? `${s.color}20` : C.bg4, border: `2px solid ${step === i ? s.color : C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.05rem", transition: "all 0.2s", marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontFamily: "monospace", fontSize: "0.56rem", color: step === i ? s.color : C.muted, fontWeight: 700, textAlign: "center", maxWidth: 55, lineHeight: 1.3 }}>{s.label}</div>
            </div>
            {i < steps.length - 1 && <div style={{ width: 16, height: 2, background: C.border, flexShrink: 0 }} />}
          </div>
        ))}
      </div>
      {step !== null && (
        <div style={{ background: C.bg4, border: `1px solid ${steps[step].color}44`, borderRadius: 10, padding: "13px 18px" }}>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "0.88rem", color: steps[step].color, marginBottom: 7 }}>{steps[step].icon} {steps[step].label}</div>
          <div style={{ fontSize: "0.82rem", color: C.text, lineHeight: 1.72, whiteSpace: "pre-line" }}>{steps[step].desc}</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════
// Main Quiz
// ══════════════════════
const QS = [
  { q: "Which organization is Canada's national SRO for investment firms, formed by merging the MFDA and IIROC?", opts: ["CSA", "CIPF", "CIRO", "OSFI"], correct: 2, fb: { y: "Correct! CIRO was formed January 1, 2023 by amalgamating the MFDA and IIROC into one unified SRO.", n: "Not quite. CIRO (formed Jan 1, 2023) is the merger of MFDA + IIROC. CSA is the umbrella of provincial regulators. CIPF is the industry insurance fund." } },
  { q: "CIPF protects client assets specifically against:", opts: ["Market losses from poor investments", "Advisor fraud or negligence", "Member firm insolvency", "Currency exchange losses"], correct: 2, fb: { y: "Correct! CIPF covers up to $1M per account category — only in the event of member firm insolvency. Market losses are not covered.", n: "Not quite. CIPF = insolvency protection only, not market losses, fraud, or bad advice. The $1M limit applies per account category." } },
  { q: "A client deposits $6,000 cash at 10AM and $5,500 cash at 3PM on the same day. Your FINTRAC obligation:", opts: ["No report — neither single deposit hits $10,000", "Suspicious Transaction Report only", "Large Cash Transaction Report — $11,500 total in 24 hours", "Terrorist Property Report"], correct: 2, fb: { y: "Correct! FINTRAC aggregates cash transactions within 24 hours. $6,000 + $5,500 = $11,500 ≥ $10,000 threshold → Large Cash Transaction Report required.", n: "Not quite. Cash transactions are aggregated over a rolling 24-hour period. $6,000 + $5,500 = $11,500 triggers the LCTR, even though neither deposit alone hit $10,000." } },
  { q: "Which clearing agency handles exchange-traded options and futures in Canada?", opts: ["CDS", "CDCC", "CSA", "TSX"], correct: 1, fb: { y: "Correct! CDCC (Canadian Derivatives Clearing Corporation) is the central counterparty for all exchange-traded derivatives — options and futures — guaranteeing every contract.", n: "Not quite. CDCC clears derivatives (options/futures). CDS clears equities and bonds. They handle different asset classes." } },
  { q: "Regulatory violations in Canada are proven on which standard?", opts: ["Beyond a reasonable doubt", "Clear and convincing evidence", "Balance of probabilities", "Absolute certainty"], correct: 2, fb: { y: "Correct! Regulatory violations use the civil standard: balance of probabilities (more likely than not). Criminal offences require 'beyond a reasonable doubt.'", n: "Not quite. Regulatory = balance of probabilities. Criminal = beyond a reasonable doubt. Only courts can impose imprisonment — not CIRO or provincial commissions." } },
  { q: "CASL requires which three elements for any commercial electronic message?", opts: ["Consent, Identification, Unsubscribe", "License, Disclosure, Privacy notice", "Signature, Date, Registration", "Approval, Audit trail, Identity"], correct: 0, fb: { y: "Correct! CASL mandates: (1) Consent from the recipient, (2) Clear identification of the sender, (3) A working unsubscribe mechanism — for all commercial emails, texts, and social media messages.", n: "Not quite. CASL = Consent + Identification + Unsubscribe. All three are mandatory or fines up to $10M per violation can apply." } },
  { q: "What is counterparty risk, and which institutions eliminate it?", opts: ["Risk of market loss — eliminated by CIPF", "Risk the other trade party defaults — eliminated by CDS and CDCC", "Risk of advisor conflict — eliminated by CIRO", "Risk of firm insolvency — eliminated by the BIA"], correct: 1, fb: { y: "Correct! Counterparty risk = the risk the other side of a trade defaults before settlement. CDS (stocks/bonds) and CDCC (derivatives) become the central counterparty to every trade, eliminating this risk.", n: "Not quite. Counterparty risk is the default risk on the opposite side of a trade. CDS and CDCC eliminate it by inserting themselves as central counterparty to every transaction." } },
  { q: "A client's complaint is unresolved after the dealer's internal process. Their next step:", opts: ["File directly with CIRO", "Sue in criminal court", "Escalate to OBSI for independent dispute resolution", "Contact OSFI"], correct: 2, fb: { y: "Correct! OBSI is the escalation path after a firm's internal complaint process fails. Free, independent, and its non-binding recommendations are rarely ignored.", n: "Not quite. OBSI handles unresolved client disputes after the dealer's internal process. It is free and independent — the correct escalation path before litigation." } },
  { q: "UMIR stands for:", opts: ["Unified Mutual Investment Regulations", "Universal Market Integrity Rules", "United Marketplace Information Requirements", "Uniform Member Investment Rules"], correct: 1, fb: { y: "Correct! Universal Market Integrity Rules — CIRO's rules for ALL trading on Canadian marketplaces, designed to prevent manipulation and ensure fair, orderly markets.", n: "Not quite. UMIR = Universal Market Integrity Rules. These govern all trading activity on Canadian exchanges and ATSs." } },
  { q: "Which practice is prohibited under NI 81-105?", opts: ["Co-op marketing within strict dollar limits", "Small promotional items (pens, branded gifts)", "Paying for an advisor's vacation disguised as a conference", "A dealer paying their own advisors a commission"], correct: 2, fb: { y: "Correct! NI 81-105 bans fund companies from paying for advisor vacations, even if framed as industry conferences. This creates an improper incentive compromising objectivity.", n: "Not quite. Advisor vacations paid by fund companies are explicitly prohibited under NI 81-105. Co-op marketing within limits, small gifts, and normal commissions are all permitted." } },
];

function MainQuiz() {
  const [cur, setCur] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const score = Object.values(answered).filter(a => a.correct).length;
  const total = QS.length;

  function answer(i) { if (answered[cur] !== undefined) return; setAnswered({ ...answered, [cur]: { chosen: i, correct: i === QS[cur].correct } }); }
  function next() { if (cur < total - 1) setCur(c => c + 1); else setDone(true); }
  function retry() { setCur(0); setAnswered({}); setDone(false); }
  const q = QS[cur], ans = answered[cur];
  const pct = Math.round(score / total * 100);

  return (
    <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 14, padding: "30px 28px 34px" }}>
      {!done ? (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: C.white }}>10-Question Practice Quiz</span>
            <span style={{ fontFamily: "monospace", fontSize: "0.7rem", color: C.muted }}>{cur + 1} / {total}</span>
          </div>
          <div style={{ height: 4, background: C.bg4, borderRadius: 2, marginBottom: 22, overflow: "hidden" }}>
            <div style={{ height: "100%", background: `linear-gradient(90deg,${C.gold},${C.blueL})`, borderRadius: 2, width: `${(cur + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "0.97rem", color: C.white, lineHeight: 1.65, marginBottom: 20 }}>{q.q}</div>
          <div style={{ display: "grid", gap: 8 }}>
            {q.opts.map((opt, i) => {
              const chosen = ans?.chosen === i, correct = i === q.correct;
              let bg = C.bg3, bdr = C.border, col = C.text;
              if (ans) { if (correct) { bg = "rgba(34,197,94,.07)"; bdr = C.greenL; col = C.greenLL; } else if (chosen) { bg = "rgba(239,68,68,.07)"; bdr = C.redL; col = C.redLL; } }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${bdr}`, borderRadius: 8, padding: "12px 17px", textAlign: "left", fontFamily: "inherit", fontSize: "0.85rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.18s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 23, height: 23, flexShrink: 0, background: C.bg4, border: `1px solid ${C.border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.63rem", color: C.muted }}>{["A","B","C","D"][i]}</span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "12px 15px", borderRadius: 8, fontSize: "0.82rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.06)" : "rgba(239,68,68,.06)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.2)" : "rgba(239,68,68,.18)"}`, color: ans.correct ? C.greenLL : C.redLL }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ marginTop: 18, background: C.gold, color: C.bg, border: "none", padding: "11px 28px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 800, borderRadius: 100, cursor: "pointer" }}>
                {cur < total - 1 ? "Next Question →" : "See Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ width: 108, height: 108, borderRadius: "50%", background: `conic-gradient(${C.gold} 0% ${pct}%, ${C.bg4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px", position: "relative" }}>
            <div style={{ position: "absolute", inset: 12, borderRadius: "50%", background: C.bg2 }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: C.gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.35rem", color: C.white, marginBottom: 8 }}>
            {score === total ? "Perfect! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.86rem", color: C.muted, marginBottom: 24 }}>
            {score === total ? "Flawless. Module 1 fully mastered." : score >= 8 ? "Excellent — review any missed questions." : "Revisit the regulators, FINTRAC thresholds, CDS/CDCC roles, and proof standards."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: C.gold, border: `1px solid ${C.gold}44`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.82rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// ══════════════════════
// MAIN EXPORT
// ══════════════════════
export default function CIREModule1() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const fn = () => {
      const h = document.documentElement;
      setProgress((h.scrollTop || document.body.scrollTop) / (h.scrollHeight - h.clientHeight) * 100);
    };
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <div style={{ background: C.bg, minHeight: "100vh", fontFamily: "'Nunito Sans', system-ui, sans-serif" }}>
      <div style={{ position: "fixed", top: 0, left: 0, height: 3, background: `linear-gradient(90deg,${C.gold},${C.blueL},${C.purpleL})`, width: `${progress}%`, zIndex: 9999, transition: "width 0.12s ease" }} />

      <div style={{ maxWidth: 880, margin: "0 auto", padding: "0 22px 96px" }}>

        {/* ── HERO ── */}
        <Reveal>
          <div style={{ padding: "72px 0 52px", borderBottom: `1px solid ${C.border}`, marginBottom: 60 }}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 20 }}>
              {[["CIRE Exam", C.gold], ["Module 1", C.blueL], ["Regulatory Framework", C.purpleL]].map(([t, c]) => (
                <span key={t} style={{ fontSize: "0.66rem", fontWeight: 800, padding: "2px 10px", borderRadius: 4, color: c, background: `${c}14`, border: `1px solid ${c}2e`, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t}</span>
              ))}
            </div>
            <h1 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(2rem,5vw,3.1rem)", fontWeight: 900, color: C.white, lineHeight: 1.12, marginBottom: 16 }}>
              Canadian Investment<br /><span style={{ color: C.gold }}>Regulatory Framework</span>
            </h1>
            <p style={{ fontSize: "0.97rem", color: C.muted, maxWidth: 560, lineHeight: 1.82, marginBottom: 32 }}>
              Regulators, registration, market structure, key laws, AML obligations, and privacy — with 4 interactive games, 12 flashcards, and a 10-question exam-style quiz, all on one page.
            </p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 24, paddingTop: 20, borderTop: `1px solid ${C.border}` }}>
              {[["🏛️","6","Regulators"],["🎮","4","Games"],["🃏","12","Flashcards"],["✦","10","Quiz questions"],["📋","6","Laws"]].map(([icon,val,lbl],i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <span>{icon}</span>
                  <span style={{ fontSize: "0.8rem", color: C.muted }}><strong style={{ color: C.text }}>{val}</strong> {lbl}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        {/* ══ SECTION 1: REGULATORS ══ */}
        <div style={{ marginBottom: 60 }}>
          <Reveal><SecLabel num="01" title="Key Regulators & Their Roles" /></Reveal>
          <Reveal>
            <h2 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(1.3rem,3vw,1.8rem)", fontWeight: 900, color: C.white, marginBottom: 10 }}>Key Regulators &amp; Their Roles</h2>
            <p style={{ fontSize: "0.88rem", color: C.muted, lineHeight: 1.82, marginBottom: 24 }}>Canada's multi-layered system — each body has a distinct, non-overlapping role. Provincial authorities sit at the top; day-to-day oversight flows through CIRO.</p>
          </Reveal>
          <div style={{ display: "grid", gap: 9 }}>
            {[
              { name: "Provincial Regulators + CSA", icon: "🏛️", color: C.gold, pts: ["Securities regulation is PROVINCIAL — no single federal regulator", "13 commissions: OSC, ASC, BCSC, AMF + 10 more", "CSA coordinates all 13 via National Instruments", "Passport System: one principal regulator for multi-province firms", "Ultimate authority — enforce securities acts, penalize violators"] },
              { name: "CIRO — National SRO", icon: "⚖️", color: C.blueL, pts: ["Formed Jan 1, 2023: MFDA + IIROC merged into one SRO", "Sets day-to-day rules for investment and mutual fund dealers", "IDPC Rules: business conduct, client relationships, suitability", "UMIR: trading integrity rules for all Canadian marketplaces", "Can fine, suspend, expel — CANNOT imprison (courts only)"] },
              { name: "CIPF — Client Insurance", icon: "🛡️", color: C.greenL, pts: ["Industry-funded (not government)", "Up to $1M per account category: general, retirement, RESP", "Triggered ONLY by member firm insolvency — not market losses", "Client assets must be held separate from firm assets"] },
              { name: "FINTRAC — AML/ATF Unit", icon: "🔍", color: C.purpleL, pts: ["Canada's financial intelligence unit under PCMLTFA", "Dealers/advisors are 'Reporting Entities'", "Large Cash Report: cash ≥$10,000 in any 24-hour period", "STR: any suspicious transaction regardless of amount", "Terrorist Property Report: no dollar threshold"] },
              { name: "OBSI — Dispute Resolution", icon: "🤝", color: C.tealL, pts: ["Free, independent service for clients", "Escalation step after dealer's internal process fails", "Non-binding recommendations — but rarely ignored"] },
              { name: "OSFI — Prudential Regulator", icon: "🏦", color: C.redL, pts: ["Regulates federally chartered banks and insurance companies", "Ensures financial stability — not market conduct", "Enforces Bank Act (including in-branch insurance sale ban)"] },
            ].map((card, i) => (
              <Reveal key={i} delay={i * 0.04}>
                <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 10, padding: "15px 19px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
                    <span>{card.icon}</span>
                    <span style={{ fontFamily: "monospace", fontSize: "0.79rem", fontWeight: 800, color: card.color }}>{card.name}</span>
                  </div>
                  {card.pts.map((p, j) => (
                    <div key={j} style={{ fontSize: "0.8rem", color: C.text, display: "flex", gap: 8, marginBottom: 4, lineHeight: 1.58 }}>
                      <span style={{ color: card.color, flexShrink: 0 }}>→</span>{p}
                    </div>
                  ))}
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* 🎮 GAME 1 */}
        <Reveal>
          <div style={{ marginBottom: 60 }}>
            <GameBadge icon="🎯" title="Game 1 — Regulator Matchup" color={C.gold} sub="Click a description, then click the matching regulator badge. Tests whether you've locked in who does what." />
            <RegulatorMatch />
          </div>
        </Reveal>

        <HR />

        {/* ══ SECTION 2: REGISTRATION ══ */}
        <div style={{ marginBottom: 60 }}>
          <Reveal><SecLabel num="02" title="Registration & Professional Obligations" /></Reveal>
          <Reveal>
            <h2 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(1.3rem,3vw,1.8rem)", fontWeight: 900, color: C.white, marginBottom: 10 }}>Registration &amp; Professional Obligations</h2>
            <p style={{ fontSize: "0.88rem", color: C.muted, lineHeight: 1.82, marginBottom: 22 }}>Gaining registration is a formal four-pillar process. All filings go through the National Registration Database (NRD).</p>
          </Reveal>
          <Reveal delay={0.08}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(166px,1fr))", gap: 9, marginBottom: 14 }}>
              {[
                { icon: "📚", label: "Proficiency", color: C.blueL, detail: "Pass required exams. For CIRE: pass the CIRE exam. No exam — no registration." },
                { icon: "💼", label: "Experience", color: C.gold, detail: "Relevant industry experience. CIRO may require a supervised period before full registration." },
                { icon: "🤝", label: "Integrity", color: C.greenL, detail: "Good character — must disclose bankruptcies, criminal records, and past regulatory sanctions." },
                { icon: "🏢", label: "Sponsorship", color: C.purpleL, detail: "Must be employed and supervised by a registered firm. No independent self-sponsorship." },
              ].map((p, i) => (
                <div key={i} style={{ background: C.bg3, border: `1px solid ${p.color}30`, borderRadius: 10, padding: "17px 14px", textAlign: "center" }}>
                  <div style={{ fontSize: "1.5rem", marginBottom: 8 }}>{p.icon}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.74rem", fontWeight: 800, color: p.color, marginBottom: 8 }}>{p.label}</div>
                  <div style={{ fontSize: "0.76rem", color: C.muted, lineHeight: 1.65 }}>{p.detail}</div>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 10, padding: "15px 19px" }}>
              {[["NRD", "Web-based system for all registration filings — both firms and individuals."], ["Firm Registration", "Must be approved by the principal provincial regulator AND CIRO before operating."], ["Form 33-109F4", "Standard individual registration application form. Filed through NRD."], ["Dealing Representative", "Category for salespeople — requires exam + dealer sponsorship + good character."]].map(([lbl, val]) => (
                <div key={lbl} style={{ display: "flex", gap: 12, marginBottom: 8, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: "monospace", fontSize: "0.68rem", color: C.blueL, width: 165, flexShrink: 0, paddingTop: 2 }}>{lbl}</span>
                  <span style={{ fontSize: "0.8rem", color: C.text, flex: 1, lineHeight: 1.6 }}>{val}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <HR />

        {/* ══ SECTION 3: MARKET STRUCTURE ══ */}
        <div style={{ marginBottom: 60 }}>
          <Reveal><SecLabel num="03" title="Market Structure: Trading, Clearing & Settlement" /></Reveal>
          <Reveal>
            <h2 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(1.3rem,3vw,1.8rem)", fontWeight: 900, color: C.white, marginBottom: 10 }}>Market Structure: Trading, Clearing &amp; Settlement</h2>
            <p style={{ fontSize: "0.88rem", color: C.muted, lineHeight: 1.82, marginBottom: 22 }}>Where securities are traded and how trades are finalized. The clearing agency model eliminates counterparty risk — the risk the other side defaults before settlement.</p>
          </Reveal>
          <Reveal delay={0.08}><TradeFlow /></Reveal>
          <Reveal delay={0.12}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(192px,1fr))", gap: 9, marginTop: 13 }}>
              {[
                { icon: "🏛️", name: "TSX / TSX-V", color: C.gold, desc: "Primary exchanges — companies list to raise capital. Provide price discovery and liquidity." },
                { icon: "🌑", name: "ATS (Dark Pools)", color: C.purpleL, desc: "Trade already-listed securities anonymously. Institutions use them to reduce market impact on large orders." },
                { icon: "⚙️", name: "CDS — T+1", color: C.tealL, desc: "Clears stocks and bonds. Settlement 1 business day after trade. Central counterparty eliminates default risk." },
                { icon: "🔒", name: "CDCC", color: C.blueL, desc: "Clears all exchange-traded derivatives (options, futures). Guarantees every contract as central counterparty." },
              ].map((c, i) => (
                <div key={i} style={{ background: C.bg3, border: `1px solid ${c.color}28`, borderRadius: 8, padding: "13px 13px" }}>
                  <div style={{ fontSize: "1.2rem", marginBottom: 7 }}>{c.icon}</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.68rem", fontWeight: 800, color: c.color, marginBottom: 5 }}>{c.name}</div>
                  <div style={{ fontSize: "0.77rem", color: C.muted, lineHeight: 1.65 }}>{c.desc}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <HR />

        {/* ══ SECTION 4: KEY LAWS ══ */}
        <div style={{ marginBottom: 60 }}>
          <Reveal><SecLabel num="04" title="Key Laws & Agencies" /></Reveal>
          <Reveal>
            <h2 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(1.3rem,3vw,1.8rem)", fontWeight: 900, color: C.white, marginBottom: 10 }}>Key Laws &amp; Agencies</h2>
            <p style={{ fontSize: "0.88rem", color: C.muted, lineHeight: 1.82, marginBottom: 22 }}>Six pieces of legislation — what each covers and your obligation under it.</p>
          </Reveal>
          <Reveal delay={0.06}>
            <div style={{ display: "grid", gap: 8, marginBottom: 18 }}>
              {[
                { code: "PCMLTFA", color: C.redL, icon: "🚫", pts: ["Makes dealers/advisors 'Reporting Entities'", "Duty to identify clients, keep records, and report to FINTRAC", "Three ML stages: Placement → Layering → Integration"] },
                { code: "Criminal Code", color: C.purpleL, icon: "⚖️", pts: ["Defines fraud, theft, insider trading as criminal offences", "Investigated by RCMP (IMET unit) — proven 'beyond a reasonable doubt'", "Only courts can impose imprisonment"] },
                { code: "PIPEDA", color: C.blueL, icon: "🔒", pts: ["Governs collection, use, and disclosure of personal information", "Consent required; data used only for stated purpose", "Clients have right to access their own information"] },
                { code: "CASL", color: C.gold, icon: "📧", pts: ["All commercial electronic messages: Consent + Identification + Unsubscribe", "Covers email, SMS, social media messages", "Fines up to $10M per violation"] },
                { code: "Bank Act", color: C.tealL, icon: "🏦", pts: ["Governs federally chartered banks", "Bans in-branch sale of most insurance products", "Prevents tying insurance to banking services"] },
                { code: "BIA", color: C.muted, icon: "📋", pts: ["Bankruptcy and Insolvency Act", "Legal framework for a firm's financial failure", "Works in tandem with CIPF to protect client assets"] },
              ].map((law, i) => (
                <div key={i} style={{ background: C.bg3, border: `1px solid ${law.color}26`, borderRadius: 8, padding: "13px 17px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 9 }}>
                    <span>{law.icon}</span>
                    <span style={{ fontFamily: "monospace", fontSize: "0.78rem", fontWeight: 800, color: law.color }}>{law.code}</span>
                  </div>
                  {law.pts.map((p, j) => (
                    <div key={j} style={{ fontSize: "0.79rem", color: C.text, display: "flex", gap: 8, marginBottom: 4, lineHeight: 1.58 }}>
                      <span style={{ color: law.color, flexShrink: 0 }}>→</span>{p}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div style={{ background: `${C.redL}08`, border: `1px solid ${C.redL}28`, borderRadius: 10, padding: "16px 18px" }}>
              <div style={{ fontFamily: "monospace", fontSize: "0.65rem", color: C.redL, letterSpacing: "0.12em", marginBottom: 11 }}>⚖️ CRITICAL DISTINCTION — REGULATORY VS. CRIMINAL</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 9 }}>
                {[{ label: "Regulatory", color: C.gold, items: ["Balance of probabilities", "CIRO / provincial commissions", "Fines, suspensions, bans", "No imprisonment"] }, { label: "Criminal", color: C.redL, items: ["Beyond a reasonable doubt", "RCMP (IMET) + courts", "Fines + imprisonment", "Requires court action"] }].map(c => (
                  <div key={c.label} style={{ background: C.bg3, borderRadius: 8, padding: "11px 14px" }}>
                    <div style={{ fontFamily: "monospace", fontSize: "0.66rem", fontWeight: 800, color: c.color, marginBottom: 8 }}>{c.label}</div>
                    {c.items.map((it, i) => <div key={i} style={{ fontSize: "0.77rem", color: C.text, display: "flex", gap: 8, marginBottom: 3 }}><span style={{ color: c.color }}>→</span>{it}</div>)}
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>

        {/* 🎮 GAME 2 */}
        <Reveal>
          <div style={{ marginBottom: 60 }}>
            <GameBadge icon="🔀" title="Game 2 — Money Laundering Stages Sorter" color={C.redL} sub="6 real-world scenarios — classify each as Placement, Layering, or Integration. Instant feedback on every answer." />
            <MLSorter />
          </div>
        </Reveal>

        {/* 🎮 GAME 3 */}
        <Reveal>
          <div style={{ marginBottom: 60 }}>
            <GameBadge icon="🏦" title="Game 3 — FINTRAC Decision Trainer" color={C.purpleL} sub="6 compliance scenarios. Choose the right FINTRAC report — or confirm no report is needed. Tests aggregation rules and suspicious indicators." />
            <FINTRACTrainer />
          </div>
        </Reveal>

        <HR />

        {/* 🎮 GAME 4 — FLASHCARDS */}
        <Reveal>
          <div style={{ marginBottom: 60 }}>
            <GameBadge icon="🃏" title="Game 4 — Flashcards (12 cards)" color={C.blueL} sub="Click to flip. Mark each as 'Got it' or 'Review'. Progress dots track your completion. Covers all key concepts from Module 1." />
            <Flashcards />
          </div>
        </Reveal>

        <HR />

        {/* ══ QUIZ ══ */}
        <div style={{ marginBottom: 56 }}>
          <Reveal><SecLabel num="QUIZ" title="10-Question Exam Practice" color={C.gold} /></Reveal>
          <Reveal>
            <h2 style={{ fontFamily: "Georgia,serif", fontSize: "clamp(1.3rem,3vw,1.8rem)", fontWeight: 900, color: C.white, marginBottom: 10 }}>Module 1 — Final Quiz</h2>
            <p style={{ fontSize: "0.88rem", color: C.muted, lineHeight: 1.82, marginBottom: 22 }}>10 exam-style questions covering regulators, FINTRAC thresholds, market infrastructure, key laws, and proof standards. Aim for 8/10 or higher.</p>
          </Reveal>
          <Reveal delay={0.08}><MainQuiz /></Reveal>
        </div>

        {/* ══ QUICK REFERENCE ══ */}
        <Reveal>
          <div style={{ marginBottom: 40 }}>
            <SecLabel num="REF" title="Key Numbers & Thresholds" color={C.muted} />
            <div style={{ background: C.bg3, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.border}`, fontFamily: "monospace", fontSize: "0.65rem", color: C.gold, letterSpacing: "0.12em" }}>QUICK REFERENCE</div>
              {[
                ["FINTRAC Large Cash Threshold", "$10,000 within any 24-hour period", C.gold],
                ["CIPF Coverage per Account Category", "Up to $1,000,000", C.greenL],
                ["CIPF Account Categories", "General · Retirement (RRSP/RRIF) · RESP", C.greenL],
                ["CDS Settlement Cycle", "T+1 — one business day after trade date", C.tealL],
                ["STR Dollar Threshold", "None — any suspicious amount qualifies", C.redL],
                ["CIRO Founded", "January 1, 2023 (MFDA + IIROC merged)", C.blueL],
                ["Regulatory Proof Standard", "Balance of Probabilities", C.gold],
                ["Criminal Proof Standard", "Beyond a Reasonable Doubt", C.redL],
                ["CASL Requirements", "Consent + Identification + Unsubscribe", C.purpleL],
                ["Number of CSA Members", "13 (all provinces and territories)", C.gold],
                ["NI 81-101 / 81-102 / 81-105", "Disclosure / Fund Operations / Sales Practices", C.blueL],
              ].map(([lbl, val, col], i, arr) => (
                <div key={i} style={{ display: "flex", gap: 12, padding: "9px 20px", borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : "none", flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: "0.78rem", color: C.muted, flex: 1, minWidth: 190 }}>{lbl}</span>
                  <span style={{ fontFamily: "monospace", fontSize: "0.78rem", fontWeight: 700, color: col }}>{val}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

      </div>
    </div>
  );
}
